import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Row } from 'react-table';
import { Scrollbars } from 'react-custom-scrollbars';
import { FleetInfo, OwnedTargetInfo, PortCatalogData, PortCatalogSearchRequest, TargetCatalogEntryInfo, TargetCatalogSearchRequest } from 'ais-facade-api/api';
import { Icon, Input, Typo, VesselIcon } from 'app/components';
import { Table } from '../Table';
import { useAlertHook, useDebounce, useFormatMoment, useOnClickOutside } from 'app/hooks';
import { useLazyQuerySearchQuery } from '../../api/search';
import { CountryFlag } from 'CountryFlag/CountryFlag';
import { useGetVesselCatalogMutation } from 'app/api/facade';
import { useGetPortCatalogMutation } from 'app/api/marine-infrastructure';
import styles from './Search.module.scss';
import moment from 'moment';
import { CustomErrorT } from 'app/types/types';

type SearchT = {
  aligment?: string;
  setInitCenter: React.Dispatch<React.SetStateAction<number[]>>;
};

export const Search = ({ aligment, setInitCenter }: SearchT) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();

  const [value, setValue] = useState<string>('');
  const debouncedValue = useDebounce<string>(value, 500);
  const [isVesselSearchResultVisible, setIsVesselSearchResultVisible] = useState<boolean>(false);
  const [isPortSearchResultVisible, setIsPortSearchResultVisible] = useState<boolean>(false);
  const [isVesselLocalSearchResultVisible, setIsVesselLocalSearchResultVisible] = useState<boolean>(false);
  const [isPortLocalSearchResultVisible, setIsPortLocalSearchResultVisible] = useState<boolean>(false);
  const [inputFocused, setInputFocused] = useState<boolean>(false);
  const [sortedVesselCatalog, setSortedVesselCatalog] = useState<TargetCatalogEntryInfo[] | null>(null);


  const [searchVessel, { ...searchVesselState }] = useLazyQuerySearchQuery();
  const [searchPort, { ...searchPortState }] = useGetPortCatalogMutation();
  const [getVesselCatalog, { data: vesselCatalog, ...vesselCatalogRequest }] = useGetVesselCatalogMutation();
  const loading = searchVesselState.isLoading || searchVesselState.isFetching || searchPortState.isLoading || vesselCatalogRequest.isLoading;

  const mmsiArray: number[] | null = JSON.parse(localStorage.getItem('mmsi_search') || 'null');
  const portsArray: string[] | null = JSON.parse(localStorage.getItem('ports_search') || 'null');

  const resultBox = useRef<HTMLDivElement>(null);

  // useEffect для сортировки каталога судов по порядку расположения-индексу в массиве сохраненных пользователем последних mmsi.
  useEffect(() => {
    if (vesselCatalog && vesselCatalog.results && mmsiArray) {
      const sortedVesselCatalog = [...vesselCatalog.results]?.sort((a, b) => {
        if (a.mmsi && b.mmsi) {
          const indexA = mmsiArray.indexOf(a.mmsi);
          const indexB = mmsiArray.indexOf(b.mmsi);
          return indexA - indexB;
        }
        return 0;
      });
      setSortedVesselCatalog(sortedVesselCatalog);
    }
  }, [vesselCatalog]);

  const vesselSearchBody: TargetCatalogSearchRequest = useMemo(
    () => ({
      filter: {
        mmsi: mmsiArray || undefined,
      },
      order: {
        direction: 'DESC',
        by: 'LUT',
      },
      pagination: {
        offset: 0,
        limit: 10,
      },
    }),
    [mmsiArray],
  );

  const portsSearchBody: PortCatalogSearchRequest = useMemo(
    () => ({
      filter: { key: portsArray || undefined },
      order: { direction: "ASC", by: "CITY_NAME" },
      pagination: { offset: 0, limit: 5 }
    }), [portsArray],
  )

  /**
   * Обработчик клика по строке таблицы результатов поиска.
   * Скрывает результаты поиска и обновляет массив MMSI.
   * @param {OwnedTargetInfo, PortCatalogData} row Выбранная строка таблицы результатов поиска.
   */


  const handleSearchTableRowClick = (row: OwnedTargetInfo & PortCatalogData) => {
    setIsVesselSearchResultVisible(false);
    setIsVesselLocalSearchResultVisible(false);
    setIsPortSearchResultVisible(false);
    setIsPortLocalSearchResultVisible(false);
    if (row?.mmsi) {
      if (typeof row?.position?.latitude === 'number' && typeof row?.position?.longitude === 'number') {
        localStorage.setItem('center', JSON.stringify([row.position.longitude, row.position.latitude]));
        setInitCenter([row.position.longitude, row.position.latitude]);
      }
      // Проверка наличия MMSI в переданной строке
      if (mmsiArray) {
        const mmsiIndex = mmsiArray.indexOf(row.mmsi);
        const updatedMmsiArray =
          mmsiIndex !== -1 // Если переданный mmsi уже есть в массиве MMSI (mmsiIndex не будет равен -1)
            ? // Обновляем массив MMSI, выталкивая дубликаты в начало массива - для отображения вверху таблицы при повторном клике на имеющийся mmsi
            [row.mmsi, ...mmsiArray.filter((mmsi) => mmsi !== row.mmsi).slice(0, 9)]
            : [row.mmsi, ...mmsiArray.slice(0, 9)]; // Если переданный mmsi отсутствует в mmsiArray, просто добавляем его в начало массива.
        localStorage.setItem('mmsi_search', JSON.stringify(updatedMmsiArray));
      }
      //логика, если MmsiArray не был инициализирован
      else if (mmsiArray === null) {
        localStorage.setItem('mmsi_search', JSON.stringify([row.mmsi]));
      }
    }

    if (row?.key) {
      if (typeof row?.lon === 'number' && typeof row?.lat === 'number') {
        localStorage.setItem('center', JSON.stringify([row.lon, row.lat]));
        setInitCenter([row.lon, row.lat]);
      }
      if (portsArray) {
        const portIndex = portsArray.indexOf(row.key);
        const updatedPortArray =
          portIndex !== -1
            ? [row.key, ...portsArray.filter((key) => key !== row.key).slice(0, 4)]
            : [row.key, ...portsArray.slice(0, 4)];
        localStorage.setItem('ports_search', JSON.stringify(updatedPortArray))
      }
      else if (portsArray === null) {
        localStorage.setItem('ports_search', JSON.stringify([row.key]));
      }
    }
  };

  const vesselTableColumns = useMemo(
    () => [
      {
        id: 'icon-header-column',
        width: 34,
        maxWidth: 34,
        minwidth: 34,
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) =>
          <div className={styles.center}>
            <VesselIcon vessel={ship} />
          </div>
      },
      {
        Header: 'MMSI',
        width: 95,
        minwidth: 95,
        maxWidth: 95,
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) =>
          typeof ship.mmsi === 'number' ? (
            <Link to={`/vessels/${ship.mmsi}`} className={styles.link}>
              {ship.mmsi}
            </Link>
          ) : (
            <>&mdash;</>
          ),
      },
      {
        Header: 'Name',
        width: 120,
        minwidth: 120,
        maxWidth: 120,
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>
            {typeof ship?.registryData?.name === 'string' ? (
              ship.registryData.name
            ) : typeof ship.staticData?.name === 'string' ? (
              ship.staticData.name
            ) : (
              <>&mdash;</>
            )}
          </>
        ),
      },
      {
        Header: 'IMO',
        width: 70,
        minwidth: 70,
        maxWidth: 70,
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>
            {typeof ship?.registryData?.imo === 'number' ? (
              ship.registryData.imo
            ) : typeof ship.staticData?.imo === 'number' ? (
              ship.staticData.imo
            ) : (
              <>&mdash;</>
            )}
          </>
        ),
      },
      {
        Header: 'CallSign',
        width: 90,
        minwidth: 90,
        maxWidth: 90,
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>
            {typeof ship?.registryData?.callSign === 'string' ? (
              ship.registryData.callSign
            ) : typeof ship.staticData?.callSign === 'string' ? (
              ship.staticData.callSign
            ) : (
              <>&mdash;</>
            )}
          </>
        ),
      },
      {
        Header: 'Flag',
        width: 120,
        minwidth: 120,
        maxWidth: 120,
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>
            {ship.meta && ship.meta.countryCode ? (
              <>
                {ship.meta.countryCode && <CountryFlag countryCode={ship.meta.countryCode?.toLocaleLowerCase()} />}
                {ship.meta.countryName} ({ship.meta.countryCode})
              </>
            ) : (
              <>&mdash;</>
            )}
          </>
        ),
      },
      {
        Header: 'Type',
        width: 140,
        minwidth: 140,
        maxWidth: 140,
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>{ship.meta && ship.meta.typeGroup ? ship.meta.typeGroup : <>&mdash;</>}</>
        ),
      },
      {
        Header: 'DataObtained',
        width: 165,
        minwidth: 165,
        maxWidth: 165,
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => {
          const formatMoment = useFormatMoment();
          return <>{ship.t ? formatMoment(moment(ship.t)) : <>&mdash;</>}</>;
        },
      },
    ],
    [],
  );

  const portTableColumns = useMemo(
    () => [
      {
        id: 'icon-header-column',
        width: 34,
        maxWidth: 34,
        minwidth: 34,
        Cell: () =>
          <div className={styles.center}>
            <Icon name='port' size="16" />
          </div>
      },
      {
        Header: 'Name',
        width: 216,
        minwidth: 216,
        maxWidth: 216,
        Cell: ({ row: { original: port } }: { row: Row<PortCatalogData> }) =>
          <Link to={`/ports/${port.key}`} className={styles.link}>
            {port.name}
          </Link>
      },
      {
        width: 70,
        minwidth: 70,
        maxWidth: 70,
        Header: 'UN/LOCODE',
        Cell: ({ row: { original: port } }: { row: Row<PortCatalogData> }) =>
          <>{port.un && port.locode ? port.un + ' ' + port.locode : <>&mdash;</>}</>
      },
      {
        width: 518,
        minwidth: 519,
        maxWidth: 518,
        Header: 'Country',
        Cell: ({ row: { original: port } }: { row: Row<PortCatalogData> }) =>
          <>
            {port.countryCode && <CountryFlag countryCode={port.countryCode?.toLocaleLowerCase()} />}
            {port.meta?.countryName} ({port.countryCode})
          </>
      },
    ],
    [],
  );

  const displayError = (error: CustomErrorT) => {
    addAlert({
      data: {
        title: 'Error',
        status: error?.status,
        detail: 'Error',
        messages: [{ description: error.detail, type: 'error' }],
      },
    })
  }

  useEffect(() => {
    if (debouncedValue.length >= 2) {
      searchPort({
        filter: { term: debouncedValue },
        order: { direction: "ASC", by: "CITY_NAME" },
        pagination: { offset: 0, limit: 5 }
      }).unwrap()
        .catch(
          (error) => {
            displayError(error);
          }
        );
      searchVessel(debouncedValue)
        .unwrap()
        .catch((error) => {
          displayError(error);
        });
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (!loading && searchVesselState.isSuccess && value.length >= 2) {
      setIsVesselSearchResultVisible(true);
    }
    if (!value) {
      setIsVesselSearchResultVisible(false);
      setIsPortSearchResultVisible(false);
    }
    if (!loading && searchPortState.isSuccess && value.length >= 2) {
      setIsPortSearchResultVisible(true);
    }
  }, [value, searchVesselState.isSuccess, searchPortState.isSuccess]);

  useEffect(() => {
    if (!value && vesselCatalog?.results) {
      setIsVesselLocalSearchResultVisible(true);
    }
    if (!value && searchPortState.data?.results) {
      setIsPortLocalSearchResultVisible(true)
    }
    if (value) {
      setIsVesselLocalSearchResultVisible(false);
      setIsPortLocalSearchResultVisible(false);
    }
  }, [value, vesselCatalog, searchPortState.data]);

  useEffect(() => {
    if (mmsiArray && value.length === 0 && inputFocused) {
      setIsVesselLocalSearchResultVisible(true);
      getVesselCatalog(vesselSearchBody).unwrap().catch((error) => {
        displayError(error);
      })

    }
    if (portsArray && value.length === 0 && inputFocused) {
      setIsPortLocalSearchResultVisible(true);
      searchPort(portsSearchBody).unwrap().catch((error) => {
        displayError(error);
      })
    }
  }, [value, inputFocused]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const charCode = String.fromCharCode(event.which).toLowerCase();
    const value = event.currentTarget.value;

    if (event.ctrlKey && charCode === 'v') {
      setValue(value);
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setInputFocused(true);
    const value = event?.currentTarget.value;

    if (value && value.length >= 2) {
      searchVessel(value);
      setIsVesselSearchResultVisible(true);
      setIsPortSearchResultVisible(true);
    }
  };

  const handleBlur = () => {
    setInputFocused(false);
  };

  const clearInput = () => {
    setValue('');
    setIsVesselSearchResultVisible(false);
  };

  const clickOutsideHandler = () => {
    setIsVesselSearchResultVisible(false);
    setIsVesselLocalSearchResultVisible(false);
    setIsPortSearchResultVisible(false);
    setIsPortLocalSearchResultVisible(false);
  };

  useOnClickOutside(resultBox, clickOutsideHandler);

  const isError = vesselCatalogRequest.isError || searchPortState.isError || searchVesselState.isError;

  return (
    <div className={styles.root} data-aligment={aligment} ref={resultBox}>
      <Input
        className={styles.search_input}
        handleClear={clearInput}
        closeble
        value={value}
        maxlength={64}
        minlength={2}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => handleKeyDown(e)}
        placeholder={t('GlobalSearchPlaceholder')}
        onFocus={(e) => handleFocus(e)}
        onBlur={handleBlur}
      />

      <div className={styles.result_box}
        data-opened={
          isVesselSearchResultVisible ||
          isVesselLocalSearchResultVisible ||
          isPortLocalSearchResultVisible ||
          isPortSearchResultVisible
        }
        data-empty={
          !searchVesselState.data ||
          searchVesselState.data?.results?.length === 0 ||
          !searchPortState.data ||
          searchPortState.data?.results?.length === 0
        }
      >
        <div className={styles.result_wrapper}>
          {isPortSearchResultVisible && searchPortState.data?.results && searchPortState.data?.results.length > 0 && !loading &&
            <>
              <div className={styles.result_title}>
                <Typo
                  size='m'
                  outline='bold'
                  value={`${t('SettingLabelShowPorts') + ', ' + t('Terminals').toLowerCase() + ' (' + searchPortState.data?.total + '):'}`}
                />
              </div>
              <Scrollbars autoHeight autoHeightMax={240}>
                <Table data={searchPortState.data.results} columns={portTableColumns} withCheckbox={false} onClick={handleSearchTableRowClick} />
              </Scrollbars>
            </>
          }
          {isPortLocalSearchResultVisible && searchPortState.data?.results && searchPortState.data?.results.length > 0 && !loading &&
            <>
              <div className={styles.result_wrapper}>
                <Typo
                  size='m'
                  outline='bold'
                  value={`${t('SettingLabelShowPorts') + ', ' + t('Terminals').toLowerCase() + ' (' + searchPortState.data?.total + '):'}`}
                />
              </div>
              <Scrollbars autoHeight autoHeightMax={396}>
                <Table data={searchPortState.data.results} columns={portTableColumns} withCheckbox={false} onClick={handleSearchTableRowClick} />
              </Scrollbars>
            </>
          }

          {isVesselSearchResultVisible && searchVesselState.data?.results && searchVesselState.data?.results.length > 0 && !loading &&
            <>
              <div className={styles.result_title}>
                <Typo size='m' outline='bold'
                  value={`${t('SettingLabelShowVessels') + ' (' + searchVesselState.data?.total + '):'}`}
                />
              </div>
              <Scrollbars autoHeight autoHeightMax={396}>
                <Table
                  data={searchVesselState.data.results}
                  columns={vesselTableColumns}
                  withCheckbox={false}
                  onClick={handleSearchTableRowClick} />
              </Scrollbars>
            </>
          }
          {isVesselLocalSearchResultVisible && sortedVesselCatalog && !vesselCatalogRequest.isLoading &&
            <>
              <div className={styles.result_wrapper}>
                <Typo size='m' outline='bold'
                  value={`${t('SettingLabelShowVessels') + ' (' + sortedVesselCatalog?.length + '):'}`}
                />
              </div>
              <Scrollbars autoHeight autoHeightMax={396}>
                <Table data={sortedVesselCatalog} columns={vesselTableColumns} withCheckbox={false} onClick={handleSearchTableRowClick} />
              </Scrollbars>
            </>
          }

          {loading && <span className={styles.loading}>{t('SearchingText')}</span>}
          {!loading &&
            searchVesselState.data?.results?.length === 0 &&
            searchPortState.data?.results?.length === 0 &&
            <p className={styles.no_data}>{t('NoResultsFound')}</p>
          }
          {!loading && isError && <div className={styles.error}>{t('ErrorOperationOfService')}</div>}
        </div>
      </div>
    </div>
  );
};
